import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Mail, Clock, MapPin, Phone, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ContactPage = () => {
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', address: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Mail, labelKey: 'contact.businessEmail', value: 'info@germanexposolutions.com', href: 'mailto:info@germanexposolutions.com' },
    { icon: Phone, labelKey: 'contact.phoneLabel', value: '+49 176 633 22 899', href: 'tel:004917663322899' },
    { icon: Clock, labelKey: 'contact.officeHours', valueKey: 'contact.officeHoursValue' },
    { icon: MapPin, labelKey: 'contact.location', value: 'Koblenz, Germany' },
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 font-heading mb-6" data-testid="contact-title">
              {t('contact.title')}
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed" data-testid="contact-subtitle">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-16 md:py-24 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="bg-white border border-slate-100 shadow-lg">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-slate-900 font-heading mb-6">
                  {t('contact.sendMessage')}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t('contact.nameLabel')}</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('contact.namePlaceholder')}
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('contact.emailLabel')}</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('contact.emailPlaceholder')}
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('contact.phoneLabel')}</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={t('contact.phonePlaceholder')}
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t('contact.messageLabel')}</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('contact.messagePlaceholder')}
                      rows={5}
                      required
                      data-testid="contact-message-input"
                    />
                  </div>

                  {/* Submit Status */}
                  {submitStatus === 'success' && (
                    <div className="flex items-center gap-2 text-green-600 bg-green-50 p-4 rounded-lg" data-testid="contact-success-message">
                      <CheckCircle className="w-5 h-5" />
                      <span>{t('contact.successMessage')}</span>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 p-4 rounded-lg" data-testid="contact-error-message">
                      <AlertCircle className="w-5 h-5" />
                      <span>{t('contact.errorMessage')}</span>
                    </div>
                  )}

                  <Button 
                    type="submit" 
                    size="lg"
                    className="w-full bg-primary-blue hover:bg-primary-blue/90 text-white"
                    disabled={isSubmitting}
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin me-2" />
                        {isRTL ? 'جاري الإرسال...' : 'Sending...'}
                      </>
                    ) : (
                      t('contact.submitBtn')
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <div className={`${isRTL ? 'text-right' : 'text-left'}`}>
              <h2 className="text-2xl font-bold text-slate-900 font-heading mb-6">
                {t('contact.contactInfo')}
              </h2>
              <p className="text-base text-slate-600 mb-8 leading-relaxed">
                {t('contact.contactInfoDesc')}
              </p>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex items-start gap-4" data-testid={`contact-info-${index}`}>
                    <div className="w-12 h-12 bg-primary-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-primary-blue" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{t(info.labelKey)}</h3>
                      {info.href ? (
                        <a href={info.href} className="text-slate-600 hover:text-primary-blue transition-colors">
                          {info.valueKey ? t(info.valueKey) : info.value}
                        </a>
                      ) : (
                        <p className="text-slate-600">{info.valueKey ? t(info.valueKey) : info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Image */}
              <div className="mt-10">
                <img
                  src="https://images.unsplash.com/photo-1766066014237-00645c74e9c6?auto=format&fit=crop&w=600&q=80"
                  alt="Customer support"
                  className="rounded-2xl shadow-lg w-full h-[250px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
